import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Calendar, Users, MapPin, FileText, Image, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function VerConvocatoria() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [convocatoria, setConvocatoria] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConvocatoria();
  }, [id]);

  const fetchConvocatoria = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convocatorias/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Convocatoria no encontrada');
      }

      const data = await response.json();
      setConvocatoria(data);
    } catch (error) {
      toast.error('Error al cargar la convocatoria');
      navigate('/convocatorias');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Activa':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Cerrada':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Próxima':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const BooleanBadge = ({ value, label }) => (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-600">{label}:</span>
      {value ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
          <Check className="h-3 w-3" /> Sí
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded">
          <X className="h-3 w-3" /> No
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  if (!convocatoria) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Detalle de Convocatoria
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/convocatorias')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <span className="text-sm text-slate-500 font-medium">{convocatoria.codigo}</span>
                <h2 className="text-2xl font-bold text-slate-900 mt-1">{convocatoria.nombre}</h2>
              </div>
              <span className={`inline-flex px-4 py-2 text-sm font-semibold rounded-lg border ${getEstadoColor(convocatoria.estado)}`}>
                {convocatoria.estado}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Descripción
              </h3>
              <p className="text-slate-600 bg-slate-50 rounded-lg p-4">{convocatoria.descripcion}</p>
            </div>

            {/* Grid Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Resolución</h4>
                  <p className="text-slate-900 font-medium">{convocatoria.resolucion}</p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Contrato</h4>
                  <p className="text-slate-900 font-medium">{convocatoria.contrato}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Fechas
                  </h4>
                  <div className="space-y-2">
                    <p className="text-slate-600">
                      <span className="font-medium">Inicio:</span> {convocatoria.fecha_inicio}
                    </p>
                    <p className="text-slate-600">
                      <span className="font-medium">Final:</span> {convocatoria.fecha_final}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Área
                  </h4>
                  <p className="text-slate-900 font-medium">{convocatoria.area || 'No especificada'}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Tipo de Beneficiario
                  </h4>
                  <p className="text-slate-900 font-medium">{convocatoria.tipo_beneficiario}</p>
                </div>

                {(convocatoria.terminos_referencia_url || convocatoria.imagen_url) && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                      <Image className="h-4 w-4" /> Archivos Adjuntos
                    </h4>
                    <div className="space-y-2">
                      {convocatoria.terminos_referencia_url && (
                        <a 
                          href={convocatoria.terminos_referencia_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline block"
                        >
                          📄 Términos de Referencia
                        </a>
                      )}
                      {convocatoria.imagen_url && (
                        <a 
                          href={convocatoria.imagen_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline block"
                        >
                          🖼️ Ver Imagen
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Configuration Options */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-slate-500 uppercase mb-4">Configuración</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <BooleanBadge value={convocatoria.postular_una_vez} label="Postular una vez" />
                <BooleanBadge value={convocatoria.convocatoria_externa} label="Externa" />
                <BooleanBadge value={convocatoria.publicar_convocatoria} label="Publicada" />
                <BooleanBadge value={convocatoria.postulacion_presencial} label="Presencial" />
                <BooleanBadge value={convocatoria.hasta_agotar_recursos} label="Hasta agotar recursos" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => navigate(`/convocatorias/${id}/editar`)}
                className="px-6 py-2 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
              >
                Editar Convocatoria
              </button>
              <button
                onClick={() => navigate('/convocatorias')}
                className="px-6 py-2 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-colors"
              >
                Volver al Listado
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
